/** Arrow geometry for the non-blocking onboarding marker. Pure: no DOM. */

/** ONB-001 baseline was 12×16. These are 2.0× that footprint (within 1.8×–2.2×). */
export const GUIDE_ARROW_LENGTH = 24;
export const GUIDE_ARROW_HALF = 16;
export const GUIDE_ARROW_GAP = 16;

export interface GuideRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface GuideViewport {
  width: number;
  height: number;
}

export type GuidePoint = 'left' | 'right' | 'up' | 'down';
export type GuideZone = 'hud' | 'world';

export interface GuideAnchor {
  x: number;
  y: number;
  point: GuidePoint;
}

export function guideSafeInsets(
  viewport: GuideViewport,
  zone: GuideZone,
): { top: number; bottom: number; side: number } {
  if (zone === 'hud') {
    return { top: 8, bottom: 8, side: 8 };
  }
  const narrow = viewport.width <= 560;
  const stacked = viewport.width <= 800;
  return {
    side: 20,
    top: narrow ? 208 : stacked ? 140 : 88,
    bottom: narrow ? 188 : stacked ? 160 : 140,
  };
}

export function guideMarkerBody(anchor: GuideAnchor): GuideRect {
  const len = GUIDE_ARROW_LENGTH;
  const half = GUIDE_ARROW_HALF;
  if (anchor.point === 'right') {
    return { left: anchor.x - len, right: anchor.x, top: anchor.y - half, bottom: anchor.y + half };
  }
  if (anchor.point === 'left') {
    return { left: anchor.x, right: anchor.x + len, top: anchor.y - half, bottom: anchor.y + half };
  }
  if (anchor.point === 'down') {
    return { left: anchor.x - half, right: anchor.x + half, top: anchor.y - len, bottom: anchor.y };
  }
  return { left: anchor.x - half, right: anchor.x + half, top: anchor.y, bottom: anchor.y + len };
}

function overlaps(a: GuideRect, b: GuideRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function inside(
  body: GuideRect,
  viewport: GuideViewport,
  insets: { top: number; bottom: number; side: number },
): boolean {
  return (
    body.left >= insets.side - 0.001 &&
    body.right <= viewport.width - insets.side + 0.001 &&
    body.top >= insets.top - 0.001 &&
    body.bottom <= viewport.height - insets.bottom + 0.001
  );
}

function candidatesFor(target: GuideRect, zone: GuideZone): GuideAnchor[] {
  const gap = GUIDE_ARROW_GAP;
  const midX = (target.left + target.right) / 2;
  const midY = (target.top + target.bottom) / 2;
  const left: GuideAnchor = { x: target.left - gap, y: midY, point: 'right' };
  const right: GuideAnchor = { x: target.right + gap, y: midY, point: 'left' };
  const above: GuideAnchor = { x: midX, y: target.top - gap, point: 'down' };
  const below: GuideAnchor = { x: midX, y: target.bottom + gap, point: 'up' };
  return zone === 'world' ? [above, below, left, right] : [left, right, above, below];
}

/**
 * Places the arrow tip just outside the target. HUD cues stay beside controls.
 * World cues prefer a downward arrow in the playfield, clear of the top and bottom HUD.
 */
export function guideMarkerAnchor(
  target: GuideRect,
  viewport: GuideViewport,
  zone: GuideZone,
): GuideAnchor {
  const insets = guideSafeInsets(viewport, zone);
  const candidates = candidatesFor(target, zone);
  const fitting = candidates.find((candidate) => {
    const body = guideMarkerBody(candidate);
    return inside(body, viewport, insets) && !overlaps(body, target);
  });
  if (fitting) {
    return fitting;
  }
  const safeMinX = insets.side + GUIDE_ARROW_LENGTH;
  const safeMaxX = Math.max(safeMinX, viewport.width - insets.side - GUIDE_ARROW_LENGTH);
  const safeMinY = insets.top + GUIDE_ARROW_LENGTH;
  const safeMaxY = Math.max(safeMinY, viewport.height - insets.bottom - GUIDE_ARROW_LENGTH);
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  for (const candidate of candidates) {
    const clamped: GuideAnchor = {
      point: candidate.point,
      x: clamp(candidate.x, safeMinX, safeMaxX),
      y: clamp(candidate.y, safeMinY, safeMaxY),
    };
    if (!overlaps(guideMarkerBody(clamped), target)) {
      return clamped;
    }
  }
  return {
    point: 'down',
    x: clamp((target.left + target.right) / 2, safeMinX, safeMaxX),
    y: clamp(target.top - GUIDE_ARROW_GAP, safeMinY, safeMaxY),
  };
}

export function ensureGuideMarker(doc: Document): HTMLElement {
  const existing = doc.getElementById('guide-marker');
  if (existing) {
    return existing;
  }
  let layer = doc.getElementById('guide-cues');
  if (!layer) {
    layer = doc.createElement('div');
    layer.id = 'guide-cues';
    const host = doc.getElementById('hud') ?? doc.getElementById('app') ?? doc.body;
    host.appendChild(layer);
  }
  const marker = doc.createElement('div');
  marker.id = 'guide-marker';
  marker.hidden = true;
  marker.setAttribute('aria-hidden', 'true');
  layer.appendChild(marker);
  return marker;
}

export function applyGuideMarker(marker: HTMLElement, anchor: GuideAnchor | null): void {
  if (!anchor) {
    marker.hidden = true;
    delete marker.dataset.point;
    return;
  }
  marker.hidden = false;
  marker.dataset.point = anchor.point;
  marker.style.left = `${anchor.x}px`;
  marker.style.top = `${anchor.y}px`;
}
